import { gql } from '@apollo/client';

/**
 * RITA-1505 (Nutzungserfassung Phase 2c) — alle Kennzahlen der Seite in EINER Operation.
 *
 * Warum ein Dokument und nicht acht Abfragen: operationLogPlugin.ts schreibt je Anfrage genau
 * eine Zeile ins Audit (sensibler Read). Acht Einzelabfragen wären acht Zeilen je Seitenaufruf
 * und je Zeitraumwechsel. Die Fehler stehen bewusst NICHT hier, sondern in GetUsageErrors:
 * sie kommen aus den Rohdaten, haben ein eigenes (kürzeres) Fenster, und alle Wurzelfelder sind
 * non-null — ein Fehler in usageErrors würde sonst die ganze Seite leeren (data: null).
 *
 * Aliase statt Filter im Client: usageActions wählt SERVERSEITIG nach Anzahl vor
 * (ACTION_PRESELECT_MAX), ein Aufruf ohne eventType liefert deshalb nicht dieselben Zeilen wie
 * zwei gefilterte. Genau deshalb haben auch Wut- und Totklicks (RITA-1520) je einen eigenen
 * Alias: in einer gemeinsamen Vorauswahl nach Anzahl fielen die selteneren Arten weg. Die fünf
 * usageSessions-Aliase brauchen je `range`, die Dimension ist ein Enum-Literal (kein String).
 * Geprüft in api/usageDocuments.schema.test.ts gegen die echte SDL.
 *
 * RITA-1523 (Versionsvergleich): der Alias `versions` holt die Ständeliste MIT — sie hängt am
 * gleichen Fenster und am gleichen Argument wie alles andere hier. Ein eigenes Dokument wäre eine
 * zusätzliche Audit-Zeile je Seitenaufruf. Der Vergleich selbst (getUsageVersionCompare.ts) steht
 * dagegen für sich: seine zwei Kennungen folgen erst aus DIESER Antwort und wechseln ohne
 * Zeitraumwechsel.
 */
const GET_USAGE_INSIGHTS = gql`
  fragment UsageActionFields on UsageActionRow {
    label
    eventType
    name
    op
    route
    count
    users
    usersEstimated
    metricAvg
    extra
    suppressed
    groups
  }

  fragment UsageSessionFields on UsageSessionRow {
    label
    value
    sessions
    users
    usersEstimated
    share
    suppressed
    groups
  }

  query GetUsageInsights($range: UsageRangeInput!) {
    usageOverview(range: $range) {
      days
      dataFrom
      dataUntil
      missingDays
      lastRollupAt
      activeUsers
      usersEstimated
      sessions
      pageViews
      pagesPerSession
      avgSessionMs
      darkShare
      apiCalls
      apiErrors
      apiErrorRate
      suppressed
    }
    usageRoutes(range: $range) {
      label
      route
      views
      users
      usersEstimated
      sessions
      entriesDirect
      avgVisibleMs
      p50VisibleMs
      p95VisibleMs
      p95Note
      visibleCount
      avgScrollPct
      bounceRate
      errors
      clicks
      suppressed
      groups
    }
    usageTransitions(range: $range) {
      label
      fromRoute
      toRoute
      count
      users
      usersEstimated
      bounceRate
      suppressed
      groups
    }
    actionsClick: usageActions(range: $range, eventType: "click", limit: 200) {
      total
      limit
      offset
      items {
        ...UsageActionFields
      }
    }
    actionsAction: usageActions(range: $range, eventType: "action", limit: 200) {
      total
      limit
      offset
      items {
        ...UsageActionFields
      }
    }
    actionsRage: usageActions(range: $range, eventType: "rage_click", limit: 100) {
      total
      limit
      offset
      items {
        ...UsageActionFields
      }
    }
    actionsDead: usageActions(range: $range, eventType: "dead_click", limit: 100) {
      total
      limit
      offset
      items {
        ...UsageActionFields
      }
    }
    usageApiOperations(range: $range) {
      label
      name
      kind
      count
      errors
      errorRate
      users
      usersEstimated
      avgMs
      p50Ms
      p95Ms
      p95Note
      topCodes
      suppressed
      groups
    }
    usageVitals(range: $range) {
      label
      route
      metric
      count
      users
      usersEstimated
      avg
      p50
      p95
      p95Note
      suppressed
      groups
    }
    sessionsDevice: usageSessions(range: $range, dimension: device_class) {
      ...UsageSessionFields
    }
    sessionsBrowser: usageSessions(range: $range, dimension: browser) {
      ...UsageSessionFields
    }
    sessionsTheme: usageSessions(range: $range, dimension: theme_mode) {
      ...UsageSessionFields
    }
    sessionsLayout: usageSessions(range: $range, dimension: theme_layout) {
      ...UsageSessionFields
    }
    sessionsViewport: usageSessions(range: $range, dimension: viewport_class) {
      ...UsageSessionFields
    }
    versions: usageVersions(range: $range) {
      dataFrom
      dataUntil
      days
      missingDays
      lastRollupAt
      versions {
        appVersion
        label
        firstSeenDay
        usersRounded
        usersEstimated
      }
      hiddenVersions
      pseudonymWindowShort
    }
    usageForms(range: $range) {
      forms {
        label
        name
        opens
        submitOk
        submitFail
        abandons
        abandonRate
        avgOpenMs
        topInvalidFields
        users
        usersEstimated
        suppressed
        groups
      }
      searches {
        count
        users
        usersEstimated
        avgQueryLen
        zeroResultShare
        avgSelectedRank
        suppressed
      }
    }
  }
`;

export default GET_USAGE_INSIGHTS;
